// English UI catalogue. English is the only language for now, but every string
// goes through i18next so translation can be added without touching components.

export const en = {
  translation: {
    app: {
      title: 'PacketLossMopped',
      tagline: 'Sequential, cable-tester network measurement',
    },
    nav: {
      test: 'Test',
      sweep: 'Sweep',
      methodology: 'Methodology',
      share: 'Shared result',
    },
    theme: {
      label: 'Theme',
      light: 'Light',
      dark: 'Dark',
      'high-contrast': 'High contrast',
      system: 'System',
    },
    common: {
      start: 'Start',
      stop: 'Stop',
      running: 'Running…',
      connecting: 'Connecting…',
      cancel: 'Cancel',
      close: 'Close',
      copy: 'Copy',
      copied: 'Copied',
      copyLink: 'Copy link',
      export: 'Export',
      exportCsv: 'CSV',
      exportJson: 'JSON',
      exportPng: 'PNG',
      save: 'Save',
      saved: 'Saved',
      share: 'Share',
      loading: 'Loading…',
      none: 'None',
      all: 'All',
      node: 'Node',
      tier: 'Tier',
      retry: 'Retry',
      back: 'Back',
      of: 'of',
    },
    tiers: {
      lan: 'LAN',
      isp: 'ISP',
      region: 'Region',
      world: 'World',
    },
    nodes: {
      pick: 'Nodes to test',
      empty: 'No nodes are available right now.',
      refresh: 'Refresh nodes',
      unhealthy: 'unavailable',
      load: 'load',
      selectTierHint: 'Nodes are tested one at a time, in tier order (LAN → ISP → Region → World). Never in parallel.',
      selectedCount: '{{count}} selected',
    },
    settings: {
      title: 'Settings',
      simple: 'Simple',
      advanced: 'Advanced',
      preset: 'Preset',
      presetCustom: 'Custom',
      sizes: 'Message sizes (bytes)',
      sizesHint: 'Comma-separated payload sizes, cycled across packets.',
      pps: 'Packets per second',
      duration: 'Duration',
      durationMs: 'Duration (ms)',
      acceptableDelay: 'Acceptable delay (ms)',
      acceptableDelayHint: 'Round trips above this count as “late”.',
      ipMode: 'IP version',
      ipModeHint: 'Force IPv4 or IPv6, or let the connection choose.',
      ipAuto: 'Auto',
      traceroute: 'Route trace',
      tracerouteHint: 'After the test, ask each node to trace the route back to you (mtr).',
      tracerouteOn: 'Trace the route back to me',
      warmup: 'Warm-up (ms)',
      warmupHint: 'Un-recorded lead-in used to prime pacing and buffers.',
      dataUsage: 'Estimated data use',
      dataUsageHint: 'Sent and echoed, per node.',
      copySettingsLink: 'Copy settings link',
    },
    run: {
      startOne: 'Start test',
      startMany: 'Test {{count}} nodes sequentially',
      stop: 'Stop test',
      sequentialNote: 'One node at a time — the network stays clean during every measurement.',
      liveTitle: 'Live',
      current: 'Now testing',
      waiting: 'Waiting',
      done: 'Done',
      sent: 'sent',
      echoed: 'echoed',
      progress: '{{sent}} / {{total}} sent',
      noNodes: 'Select at least one node to start.',
    },
    metrics: {
      totalLoss: 'Total loss',
      uploadLoss: 'Upload loss',
      downloadLoss: 'Download loss',
      late: 'Late',
      avgLatency: 'Avg latency',
      jitter: 'Jitter (RFC 3550)',
      p50: 'p50 RTT',
      p90: 'p90 RTT',
      p99: 'p99 RTT',
      minRtt: 'Min RTT',
      maxRtt: 'Max RTT',
      drift: 'Latency drift',
      wilson: '95% CI {{low}}–{{high}}',
      packets: '{{n}} packets',
      headline: 'Headline',
    },
    charts: {
      rttOverTime: 'RTT over time',
      lossOverTime: 'Loss over time',
      distribution: 'RTT distribution',
      jitter: 'Jitter',
      burst: 'Loss bursts',
      packetStrip: 'Per-packet map',
    },
    ladder: {
      title: 'Segment localisation',
      note: 'Tiers were measured back-to-back, never simultaneously.',
    },
    route: {
      title: 'Route to you',
      note: 'Reverse trace from each node back to your address, merged into one tree. Loss/latency shown per hop.',
    },
    sweep: {
      title: 'Sweep',
      intro: 'Run a matrix of size / rate cells against a node — one cell at a time.',
      template: 'Template',
      templateCustom: 'Custom',
      node: 'Node',
      sizes: 'Sizes (bytes)',
      ppsList: 'Rates (pps)',
      pairs: 'Explicit size/pps pairs',
      pairsHint: 'When set, these override the size × rate grid.',
      repeats: 'Repeats',
      order: 'Order',
      orderSizeMajor: 'Size-major',
      orderPpsMajor: 'Rate-major',
      cells: '{{count}} cells',
      start: 'Start sweep',
      stop: 'Stop sweep',
      progress: 'Cell {{index}} / {{total}}',
      progressMatrix: 'Progress',
      heatmaps: 'Heatmaps',
      lossVsSize: 'Loss vs size',
      rttVsPps: 'RTT vs rate',
      drill: 'Cell detail',
      drillHint: 'Select a completed cell to inspect its run.',
      byLoss: 'Loss',
      bySize: 'by size',
      byPps: 'by rate',
      noNode: 'Pick a node and a template to build a sweep.',
    },
    clientInfo: {
      title: 'How you connected',
      ip: 'Your IP address',
      family: 'IP version',
      network: 'Network',
      unknownNetwork: 'Unknown network',
    },
    history: {
      title: 'History',
      empty: 'No saved measurements yet.',
      save: 'Save to history',
      saved: 'Saved \u2713',
      delete: 'Delete',
      open: 'Open',
      shared: 'Shared',
    },
    methodology: {
      title: 'Methodology',
      nodesTitle: 'Measurement nodes',
    },
    share: {
      title: 'Shared measurement',
      loading: 'Loading shared result…',
      missing: 'No result id in the link.',
      readonly: 'This is a read-only view of a stored measurement.',
      openTest: 'Run your own test',
    },
    errors: {
      generic: 'Something went wrong.',
      signaling: 'Signalling error: {{error}}',
      bootstrap: 'Could not reach the coordinator. Is it running?',
    },
  },
} as const;

export type Resources = typeof en;
